// src/features/system/admin/components/CreateServiceDialog.tsx
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import AppDialog from '@/shared/components/ui/AppDialog';
import { createServiceApi, CreateServiceInput, Service } from '@/features/system/services/api/servicesApi';

interface Props { open: boolean; onOpenChange: (v: boolean) => void }

export default function CreateServiceDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateServiceInput>({ service_host: '' });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation<Service, any, CreateServiceInput>({
    mutationFn: (data) => createServiceApi(data),
    onSuccess: () => {
  qc.invalidateQueries({ queryKey: ['admin', 'services'] });
  setForm({ service_host: '' });
      setError(null);
      onOpenChange(false);
    },
    onError: (e: any) => setError(e?.message || 'Failed to create service'),
  });

  const setField = <K extends keyof CreateServiceInput>(key: K, val: CreateServiceInput[K]) => {
    setForm(f => ({ ...f, [key]: val }));
  };

  const canSave = form.service_host.trim().length > 0 && !mutation.isPending;

  return (
    <AppDialog
      open={open}
      onOpenChange={(v)=>{
  if(!v){ setForm({ service_host: '' }); setError(null); }
        onOpenChange(v);
      }}
  title="New service"
  description="Provide the service host (yymmdd_name). Name/port are derived by backend."
      size="sm"
  onConfirm={() => { if (canSave) mutation.mutate(form); }}
      confirmLabel={mutation.isPending ? 'Creating…' : 'Create service'}
      confirmDisabled={!canSave}
      isBusy={mutation.isPending}
    >
      <div className="space-y-3">
        {error && <p className="text-sm text-rose-400">{error}</p>}
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-neutral-400">Service Host *</label>
          <input
            autoFocus
            type="text"
            className="bg-neutral-800 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
            value={form.service_host}
            onChange={e => setField('service_host', e.target.value)}
            placeholder="250604_purchase_sales"
          />
          <p className="text-[11px] leading-snug text-neutral-500">
            Format: yymmdd_name. Backend derives service_name (part after first underscore), assigns id & port.
          </p>
        </div>
      </div>
    </AppDialog>
  );
}
